import { useEffect, useState } from 'react'
import useSWR from 'swr'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { fetcher } from '@/lib/fetcher'

type MasterPanelProps = {
  enabled?: boolean
}

export function MasterTimelinePanel({ enabled = true }: MasterPanelProps) {
  const [available, setAvailable] = useState(true)

  useEffect(() => {
    setAvailable(Boolean(enabled))
  }, [enabled])

  const shouldFetch = enabled && available
  const { data, isLoading, error, mutate } = useSWR(shouldFetch ? '/master/timeline' : null, fetcher, {
    refreshInterval: shouldFetch ? 5000 : 0,
    revalidateOnFocus: shouldFetch,
    onError: (err) => {
      if ((err as { status?: number } | undefined)?.status === 404) {
        setAvailable(false)
      }
    },
  })
  const entries = shouldFetch ? data?.entries ?? [] : []
  const isUnavailable = enabled && !available

  return (
    <Card className="border border-border/70 bg-background/70">
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle className="text-lg">Master timeline</CardTitle>
        <Badge variant="outline" className="text-xs">
          {entries.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {error && !isUnavailable && <p className="text-xs text-rose-300">Unable to load timeline</p>}
        {isUnavailable ? (
          <p className="text-xs text-muted-foreground">Master insights are unavailable in this environment.</p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">{isLoading ? 'Loading…' : 'No timeline entries yet.'}</p>
        ) : (
          <ul className="space-y-2">
            {entries.slice(0, 6).map((entry: any) => (
              <li key={entry.id ?? entry.timestamp ?? Math.random()} className="rounded-xl border border-border/80 bg-background/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-foreground">{entry.summary ?? entry.type}</span>
                  <Badge variant="secondary" className="text-xs">
                    {new Date(entry.timestamp ?? Date.now()).toLocaleTimeString()}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{entry.agent ?? 'agent'}</p>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="text-xs uppercase tracking-wide text-primary-foreground dark:text-amber-200 hover:underline disabled:opacity-50"
          onClick={() => mutate()}
          disabled={!available}
        >
          Refresh
        </button>
      </CardContent>
    </Card>
  )
}
